import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { api } from '../services/api';
import { formatDateTime } from '../utils/helpers';
import type {
  BestPracticesAudienceContent,
  BestPracticesContent,
  BestPracticesStep,
} from '../types';
import {
  Save,
  RefreshCw,
  Plus,
  Trash2,
  User,
  Search,
  MessageSquare,
  Send,
  Building2,
  Users,
  CheckCircle2,
  Mail,
  BarChart3,
  Link2,
  Star,
  Award,
  FileText,
  Briefcase,
  Target,
  ClipboardList,
  Rocket,
  ShieldCheck,
  Calendar,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';

// ── Icon selector ────────────────────────────────────────────────────────────
// Curated list of string IDs the backend/website understand. Keep IDs in sync
// with backend/app/services/best_practices_config.py and whatever maps these
// IDs to <Icon /> components on the public website.

const ICON_OPTIONS: { id: string; label: string; icon: LucideIcon }[] = [
  { id: 'user', label: 'User', icon: User },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'building-2', label: 'Company', icon: Building2 },
  { id: 'search', label: 'Search', icon: Search },
  { id: 'message-square', label: 'Conversation', icon: MessageSquare },
  { id: 'send', label: 'Send', icon: Send },
  { id: 'check-circle-2', label: 'Check', icon: CheckCircle2 },
  { id: 'mail', label: 'Mail', icon: Mail },
  { id: 'bar-chart-3', label: 'Chart', icon: BarChart3 },
  { id: 'link-2', label: 'Link', icon: Link2 },
  { id: 'star', label: 'Star', icon: Star },
  { id: 'award', label: 'Award', icon: Award },
  { id: 'file-text', label: 'Document', icon: FileText },
  { id: 'briefcase', label: 'Briefcase', icon: Briefcase },
  { id: 'target', label: 'Target', icon: Target },
  { id: 'clipboard-list', label: 'Checklist', icon: ClipboardList },
  { id: 'rocket', label: 'Rocket', icon: Rocket },
  { id: 'shield-check', label: 'Verified', icon: ShieldCheck },
  { id: 'calendar', label: 'Calendar', icon: Calendar },
];

const ICON_BY_ID = new Map(ICON_OPTIONS.map((option) => [option.id, option]));

function resolveIcon(iconId: string): LucideIcon {
  return ICON_BY_ID.get(iconId)?.icon ?? HelpCircle;
}

// Lowercase helper (not a component) that returns an already-built element,
// rather than handing back a component reference for a PascalCase component
// to use as a dynamic JSX tag (`<Icon />`) — keeps icon identity resolution
// out of component render bodies entirely.
function renderIcon(iconId: string, className: string): ReactNode {
  const Icon = resolveIcon(iconId);
  return <Icon className={className} />;
}

type AudienceId = 'candidate' | 'recruiter';

const AUDIENCE_TABS: { id: AudienceId; label: string; icon: LucideIcon }[] = [
  { id: 'candidate', label: 'Candidates', icon: User },
  { id: 'recruiter', label: 'Recruiters', icon: Building2 },
];

const STEPS_PER_AUDIENCE = 4;

// ── Shared field bits (matches Billing.tsx / Settings.tsx styling) ──────────

function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-1.5">
      {children}
    </label>
  );
}

const inputClass =
  'w-full px-3.5 py-2.5 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] placeholder-[#64748b] focus:outline-none focus:border-[#3b82f6] transition-colors';

function IconPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (iconId: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {ICON_OPTIONS.map((option) => {
        const isActive = option.id === value;
        const OptionIcon = option.icon;
        return (
          <button
            key={option.id}
            type="button"
            title={option.label}
            aria-label={option.label}
            aria-pressed={isActive}
            onClick={() => onChange(option.id)}
            className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-colors ${
              isActive
                ? 'border-[#3b82f6] bg-[#3b82f6]/15 text-[#3b82f6]'
                : 'border-[#252535] bg-[#1c1c2a] text-[#64748b] hover:border-[#353548] hover:text-[#94a3b8]'
            }`}
          >
            <OptionIcon className="w-4 h-4" />
          </button>
        );
      })}
    </div>
  );
}

function InstructionsEditor({
  instructions,
  onChange,
}: {
  instructions: string[];
  onChange: (instructions: string[]) => void;
}) {
  // Always keep at least one row in the editor, even if blank, so there's
  // never a "stepless" empty state the admin has to recover from with the
  // Add button alone.
  const rows = instructions.length > 0 ? instructions : [''];

  const updateLine = (lineIndex: number, value: string) => {
    onChange(rows.map((line, i) => (i === lineIndex ? value : line)));
  };

  const addLine = () => {
    onChange([...rows, '']);
  };

  const removeLine = (lineIndex: number) => {
    onChange(rows.filter((_, i) => i !== lineIndex));
  };

  return (
    <div className="space-y-2">
      {rows.map((line, lineIndex) => (
        <div key={lineIndex} className="flex items-start gap-2">
          <span className="mt-2.5 text-xs font-semibold text-[#64748b] w-5 shrink-0 text-right">
            {lineIndex + 1}.
          </span>
          <input
            type="text"
            value={line}
            onChange={(e) => updateLine(lineIndex, e.target.value)}
            placeholder="Instruction line shown in the step's detail view"
            className={`${inputClass} flex-1`}
          />
          <button
            type="button"
            onClick={() => removeLine(lineIndex)}
            disabled={rows.length <= 1}
            aria-label={`Remove instruction line ${lineIndex + 1}`}
            className="mt-0.5 w-9 h-9 shrink-0 rounded-lg border border-[#252535] bg-[#1c1c2a] text-[#64748b] flex items-center justify-center hover:border-[#ef4444]/40 hover:text-[#ef4444] transition-colors disabled:opacity-40 disabled:hover:border-[#252535] disabled:hover:text-[#64748b]"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={addLine} icon={<Plus className="w-3.5 h-3.5" />}>
        Add Line
      </Button>
    </div>
  );
}

function StepEditor({
  step,
  index,
  onChange,
}: {
  step: BestPracticesStep;
  index: number;
  onChange: (patch: Partial<BestPracticesStep>) => void;
}) {
  return (
    <div className="rounded-2xl border border-[#252535] bg-[#10101a] p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-[#1c1c2a] border border-[#252535] flex items-center justify-center shrink-0">
          {renderIcon(step.icon, 'w-4 h-4 text-[#94a3b8]')}
        </div>
        <p className="text-sm font-semibold text-[#f1f5f9]">Step {index + 1}</p>
      </div>

      <div className="space-y-4">
        <div>
          <FieldLabel>Icon</FieldLabel>
          <IconPicker value={step.icon} onChange={(icon) => onChange({ icon })} />
        </div>

        <div>
          <FieldLabel>Title</FieldLabel>
          <input
            type="text"
            value={step.title}
            onChange={(e) => onChange({ title: e.target.value })}
            className={inputClass}
          />
        </div>

        <div>
          <FieldLabel>Short Description</FieldLabel>
          <textarea
            rows={2}
            value={step.description}
            onChange={(e) => onChange({ description: e.target.value })}
            className={inputClass}
          />
          <p className="text-xs text-[#64748b] mt-1.5">Shown on the step card in the list.</p>
        </div>

        <div>
          <FieldLabel>Video URL</FieldLabel>
          <input
            type="text"
            value={step.video_url}
            onChange={(e) => onChange({ video_url: e.target.value })}
            className={inputClass}
            placeholder="https://... (leave blank to show the placeholder)"
          />
          <p className="text-xs text-[#64748b] mt-1.5">
            URL only — there is no file upload. Shown when this step is opened.
          </p>
        </div>

        <div>
          <FieldLabel>Step-by-Step Instructions</FieldLabel>
          <InstructionsEditor
            instructions={step.instructions}
            onChange={(instructions) => onChange({ instructions })}
          />
          <p className="text-xs text-[#64748b] mt-1.5">
            Shown as a numbered list when this step is opened.
          </p>
        </div>
      </div>
    </div>
  );
}

function AudienceEditor({
  audience,
  onChange,
}: {
  audience: BestPracticesAudienceContent;
  onChange: (patch: Partial<BestPracticesAudienceContent>) => void;
}) {
  const updateStep = (index: number, patch: Partial<BestPracticesStep>) => {
    const steps = audience.steps.map((step, stepIndex) =>
      stepIndex === index ? { ...step, ...patch } : step
    );
    onChange({ steps });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <FieldLabel>Label</FieldLabel>
          <input
            type="text"
            value={audience.label}
            onChange={(e) => onChange({ label: e.target.value })}
            className={inputClass}
            placeholder="e.g. FOR CANDIDATES"
          />
          <p className="text-xs text-[#64748b] mt-1.5">Small eyebrow label shown above the heading.</p>
        </div>

        <div>
          <FieldLabel>Video URL</FieldLabel>
          <input
            type="text"
            value={audience.video_url}
            onChange={(e) => onChange({ video_url: e.target.value })}
            className={inputClass}
            placeholder="https://... (leave blank to show the placeholder)"
          />
          <p className="text-xs text-[#64748b] mt-1.5">URL only — there is no file upload.</p>
        </div>
      </div>

      <div>
        <FieldLabel>Heading</FieldLabel>
        <input
          type="text"
          value={audience.heading}
          onChange={(e) => onChange({ heading: e.target.value })}
          className={inputClass}
        />
      </div>

      <div>
        <FieldLabel>Description</FieldLabel>
        <textarea
          rows={3}
          value={audience.description}
          onChange={(e) => onChange({ description: e.target.value })}
          className={inputClass}
        />
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h4 className="text-sm font-semibold text-[#f1f5f9]">Steps</h4>
          <p className="text-xs text-[#64748b]">Exactly {STEPS_PER_AUDIENCE} steps are shown on the page.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {audience.steps.map((step, index) => (
            <StepEditor
              key={index}
              step={step}
              index={index}
              onChange={(patch) => updateStep(index, patch)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function BestPractices() {
  const { toast } = useToast();
  const [content, setContent] = useState<BestPracticesContent | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState('');
  const [activeAudience, setActiveAudience] = useState<AudienceId>('candidate');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      try {
        setError(null);
        const data = await api.getBestPracticesContent();
        setContent(data);
        setInitialSnapshot(JSON.stringify({ candidate: data.candidate, recruiter: data.recruiter }));
      } catch (err: any) {
        const message = err?.message || 'Failed to load Best Practices content';
        setError(message);
        if (isRefresh) toast(message, 'error');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const updateAudience = useCallback(
    (audience: AudienceId, patch: Partial<BestPracticesAudienceContent>) => {
      setContent((prev) => (prev ? { ...prev, [audience]: { ...prev[audience], ...patch } } : prev));
    },
    []
  );

  const handleSave = useCallback(async () => {
    if (!content) return;

    for (const audience of ['candidate', 'recruiter'] as AudienceId[]) {
      const section = content[audience];
      if (!section.label.trim() || !section.heading.trim() || !section.description.trim()) {
        toast(`Fill in the label, heading, and description for ${audience === 'candidate' ? 'Candidates' : 'Recruiters'}.`, 'error');
        return;
      }
      if (section.steps.length !== STEPS_PER_AUDIENCE) {
        toast(`Each audience needs exactly ${STEPS_PER_AUDIENCE} steps.`, 'error');
        return;
      }
      const hasInvalidStep = section.steps.some((step) => !step.title.trim() || !step.description.trim());
      if (hasInvalidStep) {
        toast(`Every step needs a title and description (${audience === 'candidate' ? 'Candidates' : 'Recruiters'}).`, 'error');
        return;
      }
      const hasEmptyInstructions = section.steps.some(
        (step) => step.instructions.filter((line) => line.trim()).length === 0
      );
      if (hasEmptyInstructions) {
        toast(`Every step needs at least one instruction line (${audience === 'candidate' ? 'Candidates' : 'Recruiters'}).`, 'error');
        return;
      }
      const video = section.video_url.trim();
      if (video && !/^https?:\/\//i.test(video)) {
        toast('Video URL must start with http:// or https:// (or be left blank).', 'error');
        return;
      }
      const invalidStepVideo = section.steps.find((step) => {
        const stepVideo = step.video_url.trim();
        return stepVideo && !/^https?:\/\//i.test(stepVideo);
      });
      if (invalidStepVideo) {
        toast('Step video URLs must start with http:// or https:// (or be left blank).', 'error');
        return;
      }
    }

    try {
      setSaving(true);
      const payload = {
        candidate: {
          ...content.candidate,
          label: content.candidate.label.trim(),
          heading: content.candidate.heading.trim(),
          description: content.candidate.description.trim(),
          video_url: content.candidate.video_url.trim(),
          steps: content.candidate.steps.map((step) => ({
            icon: step.icon,
            title: step.title.trim(),
            description: step.description.trim(),
            instructions: step.instructions.map((line) => line.trim()).filter(Boolean),
            video_url: step.video_url.trim(),
          })),
        },
        recruiter: {
          ...content.recruiter,
          label: content.recruiter.label.trim(),
          heading: content.recruiter.heading.trim(),
          description: content.recruiter.description.trim(),
          video_url: content.recruiter.video_url.trim(),
          steps: content.recruiter.steps.map((step) => ({
            icon: step.icon,
            title: step.title.trim(),
            description: step.description.trim(),
            instructions: step.instructions.map((line) => line.trim()).filter(Boolean),
            video_url: step.video_url.trim(),
          })),
        },
      };
      const saved = await api.updateBestPracticesContent(payload);
      setContent(saved);
      setInitialSnapshot(JSON.stringify({ candidate: saved.candidate, recruiter: saved.recruiter }));
      toast('Best Practices content updated successfully.', 'success');
    } catch (err: any) {
      toast(err?.message || 'Failed to update Best Practices content.', 'error');
    } finally {
      setSaving(false);
    }
  }, [content, toast]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin w-8 h-8 border-2 border-[#3b82f6] border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (error && !content) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-500/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <span className="text-[#ef4444] text-xl">!</span>
            </div>
            <p className="text-[#ef4444] font-medium">Failed to load Best Practices content</p>
            <p className="text-[#64748b] text-sm mt-1">{error}</p>
            <button
              onClick={() => fetchContent()}
              className="mt-4 px-4 py-2 text-sm rounded-xl bg-[#141420] border border-[#252535] text-[#94a3b8] hover:text-[#f1f5f9] hover:border-[#353548] transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (!content) return null;

  const dirty = JSON.stringify({ candidate: content.candidate, recruiter: content.recruiter }) !== initialSnapshot;

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-[#f1f5f9]">Best Practices</h2>
            <p className="text-sm text-[#64748b] mt-1">
              Manage the candidate and recruiter tutorial copy shown on the public Best Practices page
            </p>
          </div>
          <div className="flex items-center gap-2">
            {content.updated_at && (
              <span className="text-xs text-[#64748b] hidden sm:inline">
                Last saved {formatDateTime(content.updated_at)}
              </span>
            )}
            <button
              onClick={() => fetchContent(true)}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#141420] border border-[#252535] text-sm text-[#94a3b8] hover:text-[#f1f5f9] hover:border-[#353548] transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        <Card>
          <CardHeader
            title="Page Content"
            subtitle="Changes apply to both the Candidate and Recruiter tabs on obaet.com/best-practices"
            action={
              <Button
                variant="primary"
                onClick={handleSave}
                loading={saving}
                disabled={!dirty}
                icon={<Save className="w-4 h-4" />}
              >
                Save Changes
              </Button>
            }
          />

          {/* Candidate / Recruiter tabs */}
          <div className="flex gap-1 mb-6 border-b border-[#252535] pb-px">
            {AUDIENCE_TABS.map((tab) => {
              const isActive = tab.id === activeAudience;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveAudience(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                    isActive
                      ? 'border-[#3b82f6] text-[#3b82f6]'
                      : 'border-transparent text-[#64748b] hover:text-[#94a3b8]'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <AudienceEditor
            audience={content[activeAudience]}
            onChange={(patch) => updateAudience(activeAudience, patch)}
          />
        </Card>
      </div>
    </Layout>
  );
}
